import React, { useCallback, useEffect, useState } from 'react';
import { Box, Group, Text } from '@mantine/core';
import {
  ModulePageExtraProvider,
  useModulePageExtraValue,
} from '../context/ModulePageExtraContext';
import { useLocation, useNavigate } from 'react-router-dom';
import Module from '../components/Module';
import PageHelpButton from '../components/PageHelpButton';
import LandingPageButton from '../components/LandingPageButton';
import { useDynamicImport } from '../hooks/useDynamicImport';
import { authFetch, parseJsonResponse } from '../lib/api';
import { canAccessPath, HOME_APPLIED_KEY, landingView, resolveHomeRoute } from '../lib/moduleAccess';

function menuMetaKey(meta) {
  if (!meta) return '';
  return [
    meta.component_path || '',
    meta.icon || '',
    meta.module_title || '',
    meta.module_subtitle || '',
    meta.route || '',
    meta.module_key || '',
    meta.help_available ? '1' : '0',
    meta.help_title || '',
  ].join('|');
}

const DASHBOARD_META = {
  component_path: './pages/Dashboard',
  module_title: 'Tableau de bord',
  module_subtitle: "Vue d'ensemble de l'application",
  icon: 'IconLayoutDashboard',
};

const STATIC_ROUTE_META = {
  '/profil': {
    component_path: './pages/tenant/ProfilUtilisateur',
    module_title: 'Mon profil',
    module_subtitle: 'Préférences personnelles',
    icon: 'IconUser',
  },
};

const SUB_ROUTE_META = {
  '/controles/nouveau': {
    module_title: '',
    module_subtitle: '',
    component_path: './pages/Controles',
    icon: 'IconClipboardCheck',
  },
  '/controles/nouveau/electrique': {
    module_title: '',
    module_subtitle: '',
    component_path: './pages/Controles',
    icon: 'IconBolt',
  },
};

function controleFormMeta(icon = 'IconClipboardCheck') {
  return {
    module_title: '',
    module_subtitle: '',
    component_path: './pages/Controles',
    icon,
  };
}

function resolveSubRouteMeta(path) {
  if (SUB_ROUTE_META[path]) return SUB_ROUTE_META[path];
  if (/^\/controles\/\d+\/modifier$/.test(path)) return controleFormMeta();
  if (/^\/controles\/\d+\/dupliquer$/.test(path)) return controleFormMeta('IconCopy');
  if (path === '/factures/nouveau') {
    return {
      module_title: '',
      module_subtitle: '',
      component_path: './pages/Factures',
      icon: 'IconFileInvoice',
    };
  }
  if (/^\/factures\/\d+\/modifier$/.test(path)) {
    return {
      module_title: '',
      module_subtitle: '',
      component_path: './pages/Factures',
      icon: 'IconFileInvoice',
    };
  }
  if (path === '/reglements/nouveau') {
    return {
      module_title: 'Nouveau règlement',
      module_subtitle: 'Associer un paiement à une ou plusieurs factures',
      component_path: './pages/Reglements',
      icon: 'IconCash',
    };
  }
  if (path === '/controles/relance/imprimer') {
    return {
      module_title: '',
      module_subtitle: '',
      component_path: './pages/Controles',
      icon: 'IconPrinter',
    };
  }
  if (path === '/factures/relance1/imprimer') {
    return {
      module_title: '',
      module_subtitle: '',
      component_path: './pages/Factures',
      icon: 'IconPrinter',
    };
  }
  if (path === '/factures/relance2/imprimer') {
    return {
      module_title: '',
      module_subtitle: '',
      component_path: './pages/Factures',
      icon: 'IconPrinter',
    };
  }
  if (/^\/factures\/\d+\/imprimer$/.test(path)) {
    return {
      module_title: '',
      module_subtitle: '',
      component_path: './pages/Factures',
      icon: 'IconPrinter',
    };
  }
  return null;
}

function resolveTitleExtra(menuMeta) {
  if (!menuMeta?.help_available) return null;
  const route = menuMeta.route || '/';
  return (
    <PageHelpButton
      route={route}
      moduleKey={menuMeta.module_key}
      hasHelp
      helpTitle={menuMeta.help_title}
      modalTitle={menuMeta.help_title || 'Aide'}
      tooltip="Afficher l'aide"
    />
  );
}

function AppMainContent({ user, onUserUpdate }) {
  const location = useLocation();
  const pageExtra = useModulePageExtraValue();
  const isPlanningRoute = location.pathname === '/planning';
  const navigate = useNavigate();
  const [menuMeta, setMenuMetaState] = useState(DASHBOARD_META);
  const [accessDenied, setAccessDenied] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(false);
  const [noModuleAccess, setNoModuleAccess] = useState(false);

  const setMenuMeta = useCallback((next) => {
    setMenuMetaState((prev) => (menuMetaKey(prev) === menuMetaKey(next) ? prev : next));
  }, []);

  useEffect(() => {
    const path = location.pathname;
    setAccessDenied(false);
    setNoModuleAccess(false);

    if (user && path === '/') {
      const home = resolveHomeRoute(user);
      const homeApplied = sessionStorage.getItem(HOME_APPLIED_KEY) === '1';
      if (!homeApplied) {
        sessionStorage.setItem(HOME_APPLIED_KEY, '1');
        if (home && home !== '/') {
          navigate(home, { replace: true });
          return;
        }
      }
      if (!canAccessPath(user, '/')) {
        if (home && home !== '/') {
          navigate(home, { replace: true });
          return;
        }
        setMenuMeta(null);
        setNoModuleAccess(true);
        return;
      }
    }

    if (user && user.role !== 'root' && !canAccessPath(user, path)) {
      setAccessDenied(true);
      setMenuMeta(null);
      return;
    }

    if (!path || path === '/') {
      setMenuMeta(DASHBOARD_META);
      return;
    }

    if (STATIC_ROUTE_META[path]) {
      setMenuMeta(STATIC_ROUTE_META[path]);
      return;
    }

    const subMeta = resolveSubRouteMeta(path);
    if (subMeta) {
      setMenuMeta(subMeta);
      return;
    }

    setCheckingAccess(true);
    authFetch(`/api/menu_items/by-route?path=${encodeURIComponent(path)}`)
      .then((r) => parseJsonResponse(r))
      .then((d) => {
        if (d.success) setMenuMeta(d.data);
        else {
          setMenuMeta(null);
          setAccessDenied(true);
        }
      })
      .catch(() => {
        setMenuMeta(null);
        setAccessDenied(true);
      })
      .finally(() => setCheckingAccess(false));
  }, [location.pathname, user, navigate, setMenuMeta]);

  const { component: PageComponent, icon: PageIcon, loading, error } = useDynamicImport(
    menuMeta?.component_path,
    menuMeta?.icon
  );

  if (noModuleAccess) {
    return (
      <Module title="Aucun accès" subtitle="">
        <Text c="dimmed">
          Aucun module ne vous est assigné. Contactez votre administrateur d&apos;exploitation.
        </Text>
      </Module>
    );
  }

  if (accessDenied) {
    return (
      <Module title="Accès refusé" subtitle="">
        <Text c="dimmed">Vous n&apos;avez pas accès à cette page.</Text>
      </Module>
    );
  }

  if (!menuMeta) {
    return (
      <Module title="Page introuvable" subtitle="">
        <Text c="dimmed">
          {checkingAccess
            ? 'Vérification des droits…'
            : 'Cette route n&apos;existe pas ou vous n&apos;y avez pas accès.'}
        </Text>
      </Module>
    );
  }

  const helpExtra = resolveTitleExtra(menuMeta);
  const pinRoute = landingView(location.pathname, location.search);
  const canPinLanding = location.pathname === '/' || menuMeta?.route === location.pathname;
  const subtitleExtra = canPinLanding ? (
    <LandingPageButton user={user} route={pinRoute} onUserUpdate={onUserUpdate} />
  ) : null;
  const titleExtra =
    pageExtra || helpExtra ? (
      <Group gap="sm" wrap="nowrap">
        {pageExtra}
        {helpExtra}
      </Group>
    ) : null;

  return (
    <Box style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <Module
        title={menuMeta.module_title || menuMeta.label}
        subtitle={menuMeta.module_subtitle || ''}
        icon={PageIcon}
        titleExtra={titleExtra}
        subtitleExtra={subtitleExtra}
        contentMt={isPlanningRoute ? 4 : 'md'}
      >
        {loading && <Text>Chargement…</Text>}
        {error && <Text c="red">{error}</Text>}
        {!loading && !error && PageComponent && (
          <PageComponent user={user} onUserUpdate={onUserUpdate} />
        )}
      </Module>
    </Box>
  );
}

export default function AppMain(props) {
  return (
    <ModulePageExtraProvider>
      <AppMainContent {...props} />
    </ModulePageExtraProvider>
  );
}
